<template>
  <avs-card>
    <template v-slot:title>
      <v-card-title class="pl-0">{{ $t("commons.notification") }}</v-card-title>
    </template>
    <template v-slot:content>
      <v-container>
        <v-form
          :readonly="!isEditable"
          ref="form"
        >
          <v-text-field
            v-model="notification.notification_object"
            :disabled="!isEditable"
            :rules="rules.notification_object"
            :label="$t('commons.object')"
            counter="64"
            maxlength="64"
            outlined
            dense
          ></v-text-field>
          <v-select
            v-model="notification.users"
            :disabled="!isEditable"
            :items="users"
            :rules="rules.users"
            item-text="email"
            item-value="id"
            :label="$t('commons.send_to')"
            multiple
            chips
            attach
            outlined
          ></v-select>
          <v-combobox
            chips
            multiple
            v-model="notification.external_emails"
            :rules="rules.external_emails"
            :placeholder="$t('commons.send_to_externes')"
            :items="defaultItems"
            :disabled="!isEditable"
            clearable
            outlined
            :label="$t('commons.send_to_externes')"
          >
            <template #item="{ item }">
              <v-list-item-content>{{ item }}</v-list-item-content>
              <avs-deleteButton
                :shape="{ text: true, small: true }"
                :spantext="$t('categories.nonconformity.remove_list')"
              />
            </template>
          </v-combobox>
          <div v-if="isEditable">
            <v-file-input
              v-model="notification.attachments"
              :rules="[...rules.attachments, maxTotalSize(25 * 1024 * 1024)]"
              :label="$t('commons.attachments')"
              :disabled="!isEditable"
              multiple
              outlined
              dense
            ></v-file-input>
          </div>
          <div
            class="row ml-1 mb-4 mt-2"
            v-else
          >
            <div
              v-for="(attachment, index) in notification.attachments"
              :key="index"
              class="file-icon"
            >
              <a
                :href="attachment.file"
                target="_blank"
                ><v-icon large>mdi-file</v-icon></a
              >
            </div>
          </div>
          <div style="margin-bottom: 25px">
            <label>{{ $t("commons.message") }}</label>
            <ckeditor
              :editor="editor"
              :disabled="!isEditable"
              tag-name="textarea"
              rows="5"
              v-model="notification.description"
              :config="editorConfig"
              :hint="$t('views.template.hint.description')"
              :label="$t('commons.message')"
              className="content-element"
            ></ckeditor>
          </div>
          <v-col class="d-flex justify-end gap-4">
            <avs-saveButton
              v-if="!loading"
              @click="save"
              :disabled="!isEditable"
            />
            <avs-button
              v-else
              icon
              color="secondary"
              class="spin-loading"
              :iconI="loading ? 'mdi-loading spin' : ''"
              label
              fab
              small
            />
            <avs-cancelButton @click="cancel" />
          </v-col>
        </v-form>
      </v-container>
    </template>
  </avs-card>
</template>

<script>
import { mkinternalNotification } from "@/utils/datamodels"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { InternalNotificationService, TeamService } from "@/services"
import { required, max64 } from "@/utils/validator"

export default {
  name: "NotificationEditor",
  props: {
    hasUniqueName: {
      type: Function,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    value: {
      type: Object
    }
  },
  computed: {
    // notification: {
    //   get() {
    //     return _.cloneDeep(this.value || this.getDefaultValue())
    //   }
    // },
    notification() {
      return this.value || this.getDefaultValue()
    },
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  data() {
    return {
      defaultItems: [],
      intenalNotification: mkinternalNotification(),
      loading: false,
      rules: {
        description: [required],
        users: [this.usersOrEmailsRequired],
        notification_object: [required, max64],
        attachments: [],
        external_emails: [this.usersOrEmailsRequired]
      },
      users: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [
          "paragraph",
          "heading1",
          "heading2",
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "undo",
          "redo",
          "-",
          "numberedList",
          "bulletedList"
        ]
      }
    }
  },
  created() {
    this.fetchFieldTypes()
  },
  methods: {
    fetchFieldTypes() {
      TeamService.getTeam(this, this.projectId).then(response => {
        this.users = response
      })
    },
    getDefaultValue() {
      return this.intenalNotification
    },
    usersOrEmailsRequired() {
      const hasUsers = this.notification.users && this.notification.users.length > 0
      const hasExternalEmails =
        this.notification.external_emails && this.notification.external_emails.length > 0

      if (!hasUsers && !hasExternalEmails) {
        return this.$t("messages.validator.field_required")
      }
      return true
    },
    cancel() {
      this.$emit("cancel")
    },
    async performSave(data) {
      if (data.id > 0) {
        return InternalNotificationService.put(data)
      } else {
        return InternalNotificationService.post(this.projectId, data)
      }
    },
    async save() {
      this.loading = true
      this.notification.project = this.projectId
      this.notification.lang = this.$i18n.locale

      if (this.$refs.form.validate()) {
        const formData = new FormData()
        formData.append("notification_object", this.notification.notification_object)
        formData.append("description", this.notification.description)
        formData.append("project", this.notification.project)
        formData.append("lang", this.notification.lang)
        this.notification.users.forEach(user => {
          formData.append("users", user)
        })

        // Ajout des emails externes
        this.notification.external_emails.forEach(email => {
          formData.append("external_emails", email)
        })

        this.notification.attachments.forEach(file => {
          formData.append("attachments", file, file.name)
        })
        try {
          const response = await InternalNotificationService.post(this.projectId, formData)
          this.$emit("input", response)
          this.$emit("success")
          this.loading = false
        } catch (error) {
          const message = error?.external_emails
          this.$emit("error", message)
          this.loading = false
        }
      } else {
        this.loading = false
      }
    },
    maxTotalSize(maxSize) {
      return value => {
        if (Array.isArray(value)) {
          const totalSize = value.reduce((sum, file) => sum + file.size, 0)
          if (totalSize > maxSize) {
            return "Total file size should not exceed 25MB."
          }
        }
        return true
      }
    }
  }
}
</script>

<style>
.ck.ck-editor__main > .ck-editor__editable {
  height: 200px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}

.spin-loading {
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
